import { useState, useEffect, useRef } from 'react';
import { Download, Film, Loader2, FileVideo } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Slider } from '@/components/ui/Slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { formatBytes } from '@/lib/utils';

export function VideoTranscode() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [working, setWorking] = useState(false);
  const [stage, setStage] = useState('');
  const [progress, setProgress] = useState(0);
  const [outBlob, setOutBlob] = useState<Blob | null>(null);
  const [outUrl, setOutUrl] = useState('');
  const [op, setOp] = useState<'mp4' | 'webm' | 'gif' | 'audio'>('mp4');
  const [crf, setCrf] = useState(23);
  const [resolution, setResolution] = useState('keep');
  const ffmpegRef = useRef<unknown>(null);

  useEffect(() => {
    if (pendingFile && (pendingFile.type.startsWith('video/') || pendingFile.type.startsWith('audio/'))) {
      setFile(pendingFile);
      setPendingFile(null);
    }
    return () => {
      if (outUrl) URL.revokeObjectURL(outUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getFfmpeg() {
    if (ffmpegRef.current) return ffmpegRef.current as { exec: (args: string[]) => Promise<void>; writeFile: (n: string, d: Uint8Array) => Promise<void>; readFile: (n: string) => Promise<Uint8Array>; load: (opts?: object) => Promise<void>; on: (e: string, f: (e: { progress?: number }) => void) => void; loaded: boolean };
    setStage('Loading ffmpeg…');
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');
    const ffmpeg = new FFmpeg();
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd';
    ffmpeg.on('progress', ({ progress }: { progress: number }) => {
      setProgress(Math.round(progress * 100));
    });
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegRef.current = ffmpeg;
    return ffmpeg as unknown as { exec: (args: string[]) => Promise<void>; writeFile: (n: string, d: Uint8Array) => Promise<void>; readFile: (n: string) => Promise<Uint8Array>; on: (e: string, f: (e: { progress?: number }) => void) => void };
  }

  async function run() {
    if (!file) return;
    setWorking(true);
    setProgress(0);
    setOutBlob(null);
    if (outUrl) URL.revokeObjectURL(outUrl);
    setOutUrl('');
    try {
      const ffmpeg = await getFfmpeg();
      setStage('Encoding…');
      const inputName = file.name.replace(/[^a-z0-9.]/gi, '_');
      const buf = new Uint8Array(await file.arrayBuffer());
      await ffmpeg.writeFile(inputName, buf);

      let outName = '';
      const args: string[] = ['-i', inputName];
      if (resolution !== 'keep') {
        args.push('-vf', `scale=-2:${resolution}`);
      }

      if (op === 'mp4') {
        outName = 'out.mp4';
        args.push('-c:v', 'libx264', '-preset', 'fast', '-crf', String(crf), '-c:a', 'aac', outName);
      } else if (op === 'webm') {
        outName = 'out.webm';
        args.push('-c:v', 'libvpx-vp9', '-crf', String(crf), '-b:v', '0', '-c:a', 'libopus', outName);
      } else if (op === 'gif') {
        outName = 'out.gif';
        args.push('-vf', `fps=12,scale=${resolution === 'keep' ? '480' : resolution}:-2:flags=lanczos`, outName);
      } else if (op === 'audio') {
        outName = 'out.mp3';
        args.length = 0;
        args.push('-i', inputName, '-vn', '-c:a', 'libmp3lame', '-q:a', '4', outName);
      }

      await ffmpeg.exec(args);
      const data = await ffmpeg.readFile(outName);
      const outBuf = new ArrayBuffer(data.byteLength);
      new Uint8Array(outBuf).set(data);
      const blob = new Blob([outBuf], {
        type: outName.endsWith('.mp4')
          ? 'video/mp4'
          : outName.endsWith('.webm')
          ? 'video/webm'
          : outName.endsWith('.gif')
          ? 'image/gif'
          : 'audio/mpeg',
      });
      setOutBlob(blob);
      setOutUrl(URL.createObjectURL(blob));
      toast.success('Encoded');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Transcode failed');
    } finally {
      setWorking(false);
      setStage('');
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="video-transcode"
        title="Video transcoder"
        description="Convert video & audio with ffmpeg.wasm — MP4, WebM, animated GIF, or extract audio."
        badges={
          outBlob && file ? (
            <Badge variant={outBlob.size < file.size ? 'success' : 'warning'}>
              {outBlob.size < file.size ? '−' : '+'}
              {Math.round(((outBlob.size - file.size) / file.size) * 100)}%
            </Badge>
          ) : null
        }
      />

      {!file ? (
        <Dropzone accept="video/*,audio/*" onFiles={(f) => setFile(f[0])} icon={<FileVideo size={20} />} hint="Any common video or audio format" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium truncate">{file.name}</span>
              <Button size="sm" variant="ghost" onClick={() => { setFile(null); setOutBlob(null); }}>
                Change
              </Button>
            </div>
            <Tabs value={op} onValueChange={(v) => setOp(v as typeof op)}>
              <TabsList className="w-full flex-wrap !h-auto">
                <TabsTrigger value="mp4" className="flex-1">MP4</TabsTrigger>
                <TabsTrigger value="webm" className="flex-1">WebM</TabsTrigger>
                <TabsTrigger value="gif" className="flex-1">GIF</TabsTrigger>
                <TabsTrigger value="audio" className="flex-1">Audio</TabsTrigger>
              </TabsList>
              <TabsContent value="mp4">
                <Field label="Quality (CRF)" hint={`${crf} — lower is better`} className="mt-3">
                  <Slider value={crf} onChange={setCrf} min={18} max={32} showValue={false} />
                </Field>
              </TabsContent>
              <TabsContent value="webm">
                <Field label="Quality (CRF)" hint={`${crf} — lower is better`} className="mt-3">
                  <Slider value={crf} onChange={setCrf} min={18} max={40} showValue={false} />
                </Field>
              </TabsContent>
              <TabsContent value="gif" />
              <TabsContent value="audio">
                <div className="text-xs text-fg-muted mt-3">Strips video and re-encodes audio as MP3 (VBR Q4).</div>
              </TabsContent>
            </Tabs>
            {op !== 'audio' && (
              <Field label="Resolution">
                <Select
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  options={[
                    { value: 'keep', label: 'Keep source' },
                    { value: '1080', label: '1080p' },
                    { value: '720', label: '720p' },
                    { value: '480', label: '480p' },
                    { value: '360', label: '360p' },
                  ]}
                />
              </Field>
            )}
            <Button variant="primary" className="w-full" loading={working} disabled={working} onClick={run}>
              <Film size={11} />
              {working ? `${stage} ${progress}%` : 'Encode'}
            </Button>
            {working && (
              <>
                <div className="h-1 bg-bg-inset rounded-full overflow-hidden">
                  <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="text-2xs text-fg-subtle flex items-center gap-1.5">
                  <Loader2 size={10} className="animate-spin" />
                  {stage}
                </div>
              </>
            )}
            {outBlob && (
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => saveAs(outBlob, `out.${op === 'audio' ? 'mp3' : op}`)}
              >
                <Download size={11} />
                Save · {formatBytes(outBlob.size)}
              </Button>
            )}
            <div className="text-2xs text-fg-subtle leading-relaxed border-t border-border pt-2">
              ffmpeg.wasm runs entirely in your browser. First run downloads ~25 MB of WASM core.
            </div>
          </Card>

          <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              {outUrl ? 'Output' : 'Source'}
            </div>
            <div className="bg-black min-h-[400px] flex items-center justify-center">
              {outUrl ? (
                op === 'audio' ? (
                  <audio src={outUrl} controls className="w-full max-w-md p-8" />
                ) : op === 'gif' ? (
                  <img src={outUrl} alt="gif" className="max-w-full max-h-[420px]" />
                ) : (
                  <video src={outUrl} controls className="max-w-full max-h-[420px]" />
                )
              ) : (
                <video src={URL.createObjectURL(file)} controls className="max-w-full max-h-[420px]" />
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default VideoTranscode;
